'use client';

import React, { useState } from 'react';
import styled from '@benzinga/themetron';
import { Icon } from '../Icon';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import classNames from 'classnames';

export interface PaginationProps {
  pageSize?: number;
  pageNeighbors?: number;
  totalItems?: number;
  onPageChanged?: (data: PaginationData) => void;
  buttonClassName?: string;
  disabledPrevNextButton?: boolean;
  defaultPage?: number;
}

export interface PaginationData {
  nextPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

type Page = number | 'LEFT' | 'RIGHT';

const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';

const range = (from: number, to: number, step = 1) => {
  let i = from;
  const range: number[] = [];

  while (i <= to) {
    range.push(i);
    i += step;
  }

  return range;
};

export const Pagination: React.FC<PaginationProps> = ({
  buttonClassName,
  defaultPage = 1,
  disabledPrevNextButton = false,
  onPageChanged,
  pageNeighbors: pageNeighborsProp = 0,
  pageSize = 30,
  totalItems = 0,
}) => {
  const [currentPage, setCurrentPage] = useState(defaultPage || 1);

  React.useEffect(() => {
    setCurrentPage(defaultPage);
  }, [defaultPage]);

  const totalPages = Math.ceil(totalItems / pageSize);
  const pageNeighbors = typeof pageNeighborsProp === 'number' ? Math.max(0, Math.min(pageNeighborsProp, 2)) : 0;

  const gotoPage = (page: number) => {
    const nextPage = Math.max(0, Math.min(page, totalPages));

    const paginationData = {
      nextPage,
      pageSize: pageSize,
      totalItems: totalItems,
      totalPages: totalPages,
    };

    setCurrentPage(nextPage);
    onPageChanged && onPageChanged(paginationData);
  };

  const handleClick = (page: number, e: React.MouseEvent<HTMLLIElement>) => {
    e.preventDefault();
    gotoPage(page);
  };

  const handleMoveLeft = (e: React.MouseEvent<HTMLLIElement>) => {
    e.preventDefault();
    gotoPage(currentPage - 1);
  };

  const handleMoveRight = (e: React.MouseEvent<HTMLLIElement>) => {
    e.preventDefault();
    gotoPage(currentPage + 1);
  };

  const fetchPageNumbers = () => {
    const totalNumbers = pageNeighbors * 2 + 3;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      let pages: Page[] = [];

      const leftBound = currentPage - pageNeighbors;
      const rightBound = currentPage + pageNeighbors;
      const beforeLastPage = totalPages - 1;

      const startPage = leftBound > 2 ? leftBound : 2;
      const endPage = rightBound < beforeLastPage ? rightBound : beforeLastPage;

      pages = range(startPage, endPage);

      const pagesCount = pages.length;
      const singleSpillOffset = totalNumbers - pagesCount - 1;

      const leftSpill = startPage > 2;
      const rightSpill = endPage < beforeLastPage;

      const leftSpillPage = LEFT_PAGE;
      const rightSpillPage = RIGHT_PAGE;

      if (leftSpill && !rightSpill) {
        const extraPages = range(startPage - singleSpillOffset, startPage - 1);
        pages = [leftSpillPage, ...extraPages, ...pages];
      } else if (!leftSpill && rightSpill) {
        const extraPages = range(endPage + 1, endPage + singleSpillOffset);
        pages = [...pages, ...extraPages, rightSpillPage];
      } else if (leftSpill && rightSpill) {
        pages = [leftSpillPage, ...pages, rightSpillPage];
      }

      return [1, ...pages, totalPages];
    }

    return range(1, totalPages);
  };

  if (!totalItems) return null;
  if (totalPages === 1) return null;

  const pages = fetchPageNumbers();

  return (
    <PaginationContainer className="pagination-container">
      <ul className="pagination">
        {pages.map((page: Page, index) => {
          if (page === LEFT_PAGE) {
            if (!disabledPrevNextButton) {
              return (
                <li
                  aria-label="Previous"
                  className={classNames('page-item', buttonClassName)}
                  key={index}
                  onClick={handleMoveLeft}
                >
                  <Icon className="chevron-icon" icon={faChevronLeft} />
                  <span className="sr-only">Previous</span>
                </li>
              );
            } else
              return (
                <li
                  aria-label="Dots"
                  className={classNames('page-item', 'page-item-disabled', buttonClassName)}
                  key={index}
                >
                  <span>...</span>
                </li>
              );
          }
          if (page === RIGHT_PAGE) {
            if (!disabledPrevNextButton) {
              return (
                <li
                  aria-label="Next"
                  className={classNames('page-item', buttonClassName)}
                  key={index}
                  onClick={handleMoveRight}
                >
                  <Icon className="chevron-icon" icon={faChevronRight} />
                  <span className="sr-only">Next</span>
                </li>
              );
            } else
              return (
                <li
                  aria-label="Dots"
                  className={classNames('page-item', 'page-item-disabled', buttonClassName)}
                  key={index}
                >
                  <span>...</span>
                </li>
              );
          }
          return (
            <li
              className={classNames(`page-item`, { active: currentPage === page }, buttonClassName)}
              key={index}
              onClick={e => handleClick(page, e)}
            >
              {page}
            </li>
          );
        })}
      </ul>
    </PaginationContainer>
  );
};

const PaginationContainer = styled.div`
  &.pagination-container {
    display: flex;

    ul.pagination {
      display: flex;

      li.page-item {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        margin-right: 10px;
        width: 2rem;
        height: 2rem;
        color: ${({ theme }) => theme.colorPalette.black};
        border: 1px solid ${({ theme }) => theme.colorPalette.gray200};
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
        font-size: ${({ theme }) => theme.fontSize.base};
        cursor: pointer;

        &.active {
          color: ${({ theme }) => theme.colorPalette.blue500};
          border-color: ${({ theme }) => theme.colorPalette.blue500};
        }

        &:hover {
          border-color: ${({ theme }) => theme.colorPalette.blue500};
        }
      }

      @media screen and (max-width: 500px) {
        li.page-item {
          width: 1.3rem;
          height: 1.3rem;
          font-size: ${({ theme }) => theme.fontSize.base};
          border-color: transparent;

          &.active {
            border-color: ${({ theme }) => theme.colorPalette.blue500};
          }

          &:hover {
            color: ${({ theme }) => theme.colorPalette.blue500};
            border-color: transparent;
          }
        }
      }

      li.page-item-disabled {
        cursor: default;
      }
    }
    .sr-only {
      display: none;
    }
  }
`;

export default Pagination;
